import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getConfig } from '../config/environment';
import { hasAuthorizedContentAdminToken } from '../utils/adminToken';

const ColorThemeContext = createContext(undefined);

const isAdminSession = () => hasAuthorizedContentAdminToken(getConfig().tlfId);
const siteConfigColorTheme = () => {
  if (typeof window !== 'undefined' && window.SITE_CONFIG) {
    return window.SITE_CONFIG.defaults?.color_theme || 'blue';
  }
  return 'blue';
};

const predefinedThemes = {
  blue: {
    id: 'blue',
    name: 'Ocean Blue',
    primary: '#0EA5E9',
    secondary: '#06B6D4',
    accent: '#22D3EE',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    gradientStart: '#0EA5E9',
    gradientEnd: '#06B6D4'
  },
  emerald: {
    id: 'emerald',
    name: 'Emerald Forest',
    primary: '#059669',
    secondary: '#10B981',
    accent: '#34D399',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    gradientStart: '#059669',
    gradientEnd: '#10B981'
  },
  violet: {
    id: 'violet',
    name: 'Midnight Violet',
    primary: '#7C3AED',
    secondary: '#8B5CF6',
    accent: '#A78BFA',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    gradientStart: '#7C3AED',
    gradientEnd: '#8B5CF6'
  },
  amber: {
    id: 'amber',
    name: 'Golden Sunset',
    primary: '#EA580C',
    secondary: '#F59E0B',
    accent: '#FBBF24',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    gradientStart: '#EA580C',
    gradientEnd: '#F59E0B'
  },
  rose: {
    id: 'rose',
    name: 'Ruby Rose',
    primary: '#DC2626',
    secondary: '#EF4444',
    accent: '#F87171',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    gradientStart: '#DC2626',
    gradientEnd: '#EF4444'
  },
  slate: {
    id: 'slate',
    name: 'Professional Slate',
    primary: '#475569',
    secondary: '#64748B',
    accent: '#0EA5E9',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    gradientStart: '#475569',
    gradientEnd: '#64748B'
  },
  teal: {
    id: 'teal',
    name: 'Fresh Teal',
    primary: '#0D9488',
    secondary: '#14B8A6',
    accent: '#2DD4BF',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    gradientStart: '#0D9488',
    gradientEnd: '#14B8A6'
  },
  superball: {
    id: 'superball',
    name: 'Superball Gold',
    primary: '#F59E0B',
    secondary: '#FBBF24',
    accent: '#FCD34D',
    success: '#10B981',
    warning: '#F97316',
    error: '#EF4444',
    gradientStart: '#F59E0B',
    gradientEnd: '#FBBF24'
  }
};

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

function lightenColor(hex, percent) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.min(255, Math.floor(rgb.r + (255 - rgb.r) * percent));
  const g = Math.min(255, Math.floor(rgb.g + (255 - rgb.g) * percent));
  const b = Math.min(255, Math.floor(rgb.b + (255 - rgb.b) * percent));

  return rgbToHex(r, g, b);
}

function darkenColor(hex, percent) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.floor(rgb.r * (1 - percent));
  const g = Math.floor(rgb.g * (1 - percent));
  const b = Math.floor(rgb.b * (1 - percent));

  return rgbToHex(r, g, b);
}

function getContrastColor(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#000000';

  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

const CUSTOM_THEME_PREFIX = 'custom:';

function isCustomThemeId(id) {
  return typeof id === 'string'
    && id.startsWith(CUSTOM_THEME_PREFIX)
    && /^#?[0-9a-f]{6}$/i.test(id.slice(CUSTOM_THEME_PREFIX.length));
}

function buildCustomTheme(id) {
  const raw = id.slice(CUSTOM_THEME_PREFIX.length);
  const primary = raw.startsWith('#') ? raw : `#${raw}`;
  const secondary = lightenColor(primary, 0.12);
  const accent = lightenColor(primary, 0.28);
  return {
    id,
    name: 'Custom',
    primary,
    secondary,
    accent,
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    gradientStart: primary,
    gradientEnd: secondary
  };
}

function isValidThemeId(id) {
  return Boolean(predefinedThemes[id]) || isCustomThemeId(id);
}

function resolveTheme(id) {
  if (predefinedThemes[id]) return predefinedThemes[id];
  if (isCustomThemeId(id)) return buildCustomTheme(id);
  return predefinedThemes.blue;
}

export function ColorThemeProvider({ children }) {
  const [colorTheme, setColorTheme] = useState(() => {
    try {
      // Admin sessions use localStorage for draft preview persistence
      if (isAdminSession()) {
        const saved = localStorage.getItem('colorTheme');
        if (saved && isValidThemeId(saved)) return saved;
      }
      // Non-admin: check cached published theme before falling back to SITE_CONFIG
      const cached = localStorage.getItem('site_color_theme');
      if (cached && isValidThemeId(cached)) return cached;
      return siteConfigColorTheme();
    } catch (error) {
      console.error('Error reading color theme:', error);
      return 'blue';
    }
  });

  const applyThemeToDOM = (theme) => {
    const root = document.documentElement;

    root.style.setProperty('--charity-primary', theme.primary);
    root.style.setProperty('--charity-primary-light', lightenColor(theme.primary, 0.2));
    root.style.setProperty('--charity-primary-lighter', lightenColor(theme.primary, 0.4));
    root.style.setProperty('--charity-primary-lightest', lightenColor(theme.primary, 0.6));
    root.style.setProperty('--charity-primary-dark', darkenColor(theme.primary, 0.2));
    root.style.setProperty('--charity-primary-darker', darkenColor(theme.primary, 0.4));
    root.style.setProperty('--charity-primary-darkest', darkenColor(theme.primary, 0.6));
    root.style.setProperty('--charity-primary-contrast', getContrastColor(theme.primary));

    root.style.setProperty('--charity-secondary', theme.secondary);
    root.style.setProperty('--charity-secondary-light', lightenColor(theme.secondary, 0.2));
    root.style.setProperty('--charity-secondary-lighter', lightenColor(theme.secondary, 0.4));
    root.style.setProperty('--charity-secondary-lightest', lightenColor(theme.secondary, 0.6));
    root.style.setProperty('--charity-secondary-dark', darkenColor(theme.secondary, 0.2));
    root.style.setProperty('--charity-secondary-darker', darkenColor(theme.secondary, 0.4));
    root.style.setProperty('--charity-secondary-darkest', darkenColor(theme.secondary, 0.6));
    root.style.setProperty('--charity-secondary-contrast', getContrastColor(theme.secondary));

    root.style.setProperty('--charity-accent', theme.accent);
    root.style.setProperty('--charity-accent-light', lightenColor(theme.accent, 0.2));
    root.style.setProperty('--charity-accent-lighter', lightenColor(theme.accent, 0.4));
    root.style.setProperty('--charity-accent-lightest', lightenColor(theme.accent, 0.6));
    root.style.setProperty('--charity-accent-dark', darkenColor(theme.accent, 0.2));
    root.style.setProperty('--charity-accent-darker', darkenColor(theme.accent, 0.4));
    root.style.setProperty('--charity-accent-darkest', darkenColor(theme.accent, 0.6));
    root.style.setProperty('--charity-accent-contrast', getContrastColor(theme.accent));

    root.style.setProperty('--charity-success', theme.success);
    root.style.setProperty('--charity-warning', theme.warning);
    root.style.setProperty('--charity-error', theme.error);

    root.style.setProperty('--charity-gradient-start', theme.gradientStart);
    root.style.setProperty('--charity-gradient-end', theme.gradientEnd);
  };

  // Apply theme to DOM immediately during first render to prevent FOUC.
  const initializedRef = useRef(false);
  if (!initializedRef.current) {
    initializedRef.current = true;
    applyThemeToDOM(resolveTheme(colorTheme));
  }

  useEffect(() => {
    const theme = resolveTheme(colorTheme);
    applyThemeToDOM(theme);

    // Only persist to localStorage during admin sessions
    if (isAdminSession()) {
      try { localStorage.setItem('colorTheme', colorTheme); } catch {}
    }
  }, [colorTheme]);

  // Listen for publish/discard to reset to server values
  useEffect(() => {
    const handleReset = () => {
      try { localStorage.removeItem('colorTheme'); } catch {}
      const serverTheme = siteConfigColorTheme();
      // Fall back to cached published theme if SITE_CONFIG wasn't updated yet
      const cached = (() => { try { return localStorage.getItem('site_color_theme'); } catch { return null; } })();
      const resolved = (serverTheme && serverTheme !== 'blue') ? serverTheme : (cached || serverTheme);
      setColorTheme(resolved);
      applyThemeToDOM(resolveTheme(resolved));
    };
    window.addEventListener('appearanceReset', handleReset);
    return () => window.removeEventListener('appearanceReset', handleReset);
  }, []);

  // Sync from DraftContext appearance changes (Draft is a child provider; use event bus).
  useEffect(() => {
    const handleDraftSync = (event) => {
      if (!isAdminSession()) return;
      const next = event?.detail?.appearance?.colorTheme;
      if (typeof next === 'string' && next && isValidThemeId(next) && next !== colorTheme) {
        try { localStorage.setItem('colorTheme', next); } catch {}
        setColorTheme(next);
      }
    };
    window.addEventListener('appearanceDraftSync', handleDraftSync);
    return () => window.removeEventListener('appearanceDraftSync', handleDraftSync);
  }, [colorTheme]);

  const changeColorTheme = (themeId) => {
    if (isValidThemeId(themeId)) {
      setColorTheme(themeId);
      applyThemeToDOM(resolveTheme(themeId));
      if (isAdminSession()) {
        try { localStorage.setItem('colorTheme', themeId); } catch {}
      }
    }
  };

  const contextValue = {
    colorTheme,
    changeColorTheme,
    availableThemes: Object.values(predefinedThemes),
    currentTheme: resolveTheme(colorTheme)
  };

  return (
    <ColorThemeContext.Provider value={contextValue}>
      {children}
    </ColorThemeContext.Provider>
  );
}

export function useColorTheme() {
  const context = useContext(ColorThemeContext);
  if (context === undefined) {
    throw new Error('useColorTheme must be used within a ColorThemeProvider');
  }
  return context;
}
